/**
 * Where the Donor Portal lives.
 *
 * REACT_APP_DONOR_PORTAL_DOMAIN names the donors' own domain, e.g.
 * dev.hub.masjidal.com (with or without https://). On that domain the portal
 * owns the whole site and lives at its root: /, /login, /auth/callback. The
 * Donation Portal's admin pages are not served there at all.
 *
 * Anywhere else the portal lives under /donor-portal. With a domain configured,
 * that old address only forwards to the same page on the donor domain, so
 * donors have one address and Cognito only needs that domain allowlisted.
 * Without one -- localhost, typically -- the portal works under /donor-portal
 * exactly as before.
 *
 * Every donor-portal link and redirect is built here. A hard-coded
 * "/donor-portal/..." anywhere else is a bug on the donor domain.
 */

const LEGACY_BASE = '/donor-portal';

/** "https://dev.hub.masjidal.com/" -> { host: "dev.hub.masjidal.com", origin: "https://dev.hub.masjidal.com" } */
const configuredDomain = () => {
    const raw = (process.env.REACT_APP_DONOR_PORTAL_DOMAIN || '').trim();
    if (!raw) return null;

    const scheme = /^http:\/\//i.test(raw) ? 'http' : 'https';
    const host = raw
        .replace(/^https?:\/\//i, '')
        .replace(/\/.*$/, '')
        .toLowerCase();
    return host ? { host, origin: `${scheme}://${host}` } : null;
};

/** The current page's host, with the port only if the configured one has one. */
const currentHost = (domain) => {
    const loc = window.location || {};
    const value = domain.host.includes(':') ? loc.host : loc.hostname;
    return (value || '').toLowerCase();
};

/** True on the donor domain itself, where the portal is the whole site. */
export const isDonorPortalDomain = () => {
    const domain = configuredDomain();
    return !!domain && currentHost(domain) === domain.host;
};

/**
 * True on any other host while a donor domain is configured: /donor-portal here
 * should send the donor there rather than render.
 */
export const shouldMoveToDonorDomain = () =>
    !!configuredDomain() && !isDonorPortalDomain();

/** "" on the donor domain, "/donor-portal" everywhere else. */
export const portalBase = () => (isDonorPortalDomain() ? '' : LEGACY_BASE);

/**
 * An in-app path to a portal page.
 *
 *   portalPath()                 "/"               or "/donor-portal"
 *   portalPath('login')          "/login"          or "/donor-portal/login"
 *   portalPath('auth/callback')  "/auth/callback"  or "/donor-portal/auth/callback"
 */
export const portalPath = (page = '') => {
    const suffix = page ? `/${String(page).replace(/^\/+/, '')}` : '';
    return `${portalBase()}${suffix}` || '/';
};

/** True for a path that belongs to the portal at its current base. */
export const isPortalPath = (path) => {
    const base = portalBase();
    if (!base) return path.startsWith('/');
    return path === base || path.startsWith(`${base}/`) || path.startsWith(`${base}?`);
};

/**
 * The donor-domain URL for a /donor-portal address on another host:
 * /donor-portal/login?x=1 -> https://dev.hub.masjidal.com/login?x=1.
 * Null when no donor domain is configured.
 */
export const donorDomainUrlFor = (pathname = '', search = '', hash = '') => {
    const domain = configuredDomain();
    if (!domain) return null;

    const rest = pathname.startsWith(LEGACY_BASE)
        ? pathname.slice(LEGACY_BASE.length)
        : '';
    const path = rest.startsWith('/') ? rest : `/${rest}`;
    return `${domain.origin}${path}${search}${hash}`;
};

/**
 * Where a link INTO the portal should point from a Donation Portal page: the
 * donor domain when one is configured, otherwise the in-app path.
 */
export const portalEntryUrl = () => donorDomainUrlFor(LEGACY_BASE) || LEGACY_BASE;
